var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useState } from 'react';
import { SUPPORT_ATTACHMENT_DISPOSITION, SUPPORT_SCAN_RECHECK_INTERVAL_MS, SUPPORT_SCAN_RECHECK_WINDOW_MS } from '../../../features/AIChatWidget/constants/support';
import { AttachmentScanError, scanStateOf } from '../../../features/AIChatWidget/helpers/attachmentScan';
/**
 * Where a support attachment is in its antivirus scan, and the re-check while it is pending.
 *
 * The verdict starts from the message (`scanStatus`) and follows it when a newer one arrives. A
 * file still `scanning` is re-checked every few seconds for a couple of minutes by asking the
 * attachment route for it (the one authority: it answers 409 SCAN_PENDING until the scan is
 * done): a URL means clean, SCAN_FAILED means it never will be, and a scan that has not finished
 * by the end of the window is reported as one that could not be checked, never left spinning.
 * The customer's own not-yet-sent upload (`source: 'upload'`) is not on that route; its scan is
 * driven by the upload hook, so it is not polled here.
 */
export function useAttachmentScan(attachment, resolveUrl) {
    const [state, setState] = useState(() => scanStateOf(attachment.scanStatus));
    // A newer verdict from the message (history refetched, a live event) replaces our conclusion.
    useEffect(() => setState(scanStateOf(attachment.scanStatus)), [attachment.scanStatus]);
    const { id, source } = attachment;
    const canRecheck = state === 'scanning' && resolveUrl !== undefined && source !== 'upload';
    useEffect(() => {
        if (!canRecheck || !resolveUrl)
            return undefined;
        let cancelled = false;
        let timer = null;
        const startedAt = Date.now();
        const check = () => __awaiter(this, void 0, void 0, function* () {
            try {
                yield resolveUrl(id, {
                    forceRefresh: true,
                    disposition: SUPPORT_ATTACHMENT_DISPOSITION.INLINE,
                    source: source !== null && source !== void 0 ? source : 'support'
                });
                if (!cancelled)
                    setState('ready');
                return;
            }
            catch (error) {
                if (cancelled)
                    return;
                if (error instanceof AttachmentScanError && error.state === 'unchecked') {
                    setState('unchecked');
                    return;
                }
                // Still pending, or a request that failed for another reason: look again until time is up.
            }
            if (Date.now() - startedAt >= SUPPORT_SCAN_RECHECK_WINDOW_MS)
                setState('unchecked');
            else
                timer = setTimeout(runCheck, SUPPORT_SCAN_RECHECK_INTERVAL_MS); // eslint-disable-line @typescript-eslint/no-use-before-define
        });
        function runCheck() {
            void check();
        }
        timer = setTimeout(runCheck, SUPPORT_SCAN_RECHECK_INTERVAL_MS);
        return () => {
            cancelled = true;
            if (timer !== null)
                clearTimeout(timer);
        };
    }, [canRecheck, resolveUrl, id, source]);
    const absorb = useCallback((error) => {
        if (!(error instanceof AttachmentScanError))
            return false;
        setState(error.state);
        return true;
    }, []);
    return { state, absorb };
}
